'use client'

import type { ComponentProps, ReactNode } from 'react'
import { clsx } from 'clsx'

type Variant = 'primary' | 'secondary' | 'success' | 'danger' | 'outlined' | 'inverted'
type Size = 'sm' | 'md' | 'lg' | 'xl' | 'icon'

const variants: Record<Variant, string> = {
  primary: 'bg-accent-500 text-white',
  secondary: 'border border-accent-500 text-accent-500',
  success: 'bg-success-500 text-white',
  danger: 'bg-error-500 text-white',
  outlined: 'border border-neutral-700 text-neutral-700',
  inverted: 'bg-white text-primary-500',
}

const sizes: Record<Size, string> = {
  sm: 'h-7 gap-2 px-3 text-btn-sm',
  md: 'h-9 gap-2 px-6 text-btn-md',
  lg: 'h-12 gap-3 px-8 text-btn-lg',
  xl: 'h-[3.5625rem] gap-3 px-8 text-btn-lg',
  // Square, no label: direction swap, row actions. See IconButton.
  icon: 'size-9',
}

/** `ComponentProps` rather than `ButtonHTMLAttributes`: it carries `ref`. */
type Props = ComponentProps<'button'> & {
  variant?: Variant
  size?: Size
  /** Leading 16x16 icon; must be `aria-hidden` - the label carries the name. */
  icon?: ReactNode
}

export function Button({
  variant = 'primary',
  size = 'md',
  icon,
  className,
  children,
  // Buttons inside the consignment <form> must not submit it unless asked to.
  type = 'button',
  ...props
}: Props) {
  return (
    <button
      type={type}
      // Styling comes from the classes; these are for tests and devtools, so neither has
      // to assert on a class string.
      data-variant={variant}
      data-size={size}
      className={clsx(
        'inline-flex cursor-pointer items-center justify-center rounded-full focus-visible:focus-ring',
        'disabled:cursor-not-allowed disabled:border-transparent disabled:bg-neutral-400 disabled:text-white',
        variants[variant],
        sizes[size],
        className
      )}
      {...props}
    >
      {icon}
      {children === undefined ? null : <span className="cap-band">{children}</span>}
    </button>
  )
}
